import { Button, Card, CardContent } from '@flexui-io/flexui-components';
import { SliderAsyncButton } from '@slider/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Copy, Fingerprint, ShieldCheck, Smartphone } from 'lucide-react';
import QRCode from 'qrcode';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ApiProblemError } from '../app/api/slider-fetch';
import { AuthPage } from '../features/auth/auth-appearance';
import { OneTimeCodeField } from '../features/auth/one-time-code-field';
import { resolveReturnPath } from '../features/auth/session-guard';
import {
  serializeRegistrationCredential,
  toCredentialCreationOptions,
} from '../features/auth/webauthn';
import {
  createPasskeyRegistrationOptions,
  useSetupTotp,
  useVerifyTotpSetup,
  verifyPasskeyRegistration,
} from '../generated/api/auth/auth';
import type { SetupTotp200 } from '../generated/api/models';

type CodeFields = { code: string };

const MfaEnrollmentSearchSchema = z.object({
  next: z.string().max(2048).optional().catch(undefined),
});

export const Route = createFileRoute('/mfa-enrollment')({
  component: MfaEnrollmentPage,
  validateSearch: zodValidator(MfaEnrollmentSearchSchema),
});

function enrollmentErrorKey(error: unknown): string {
  if (!(error instanceof ApiProblemError)) return 'auth.errorGeneric';
  if (error.code === 'AUTHENTICATION_REQUIRED') return 'auth.errorSessionExpired';
  if (error.code === 'AUTH_MFA_INVALID_CODE') return 'auth.errorInvalidCode';
  if (error.code === 'AUTH_PASSKEY_VERIFICATION_FAILED') return 'auth.errorPasskey';
  return 'auth.errorGeneric';
}

function MfaEnrollmentPage() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  // Enrollment continues to wherever the sign-in guard was originally headed.
  const continueToManagement = () => {
    const destination = resolveReturnPath(next);
    void navigate({ href: destination.path, replace: destination.fromHandoff });
  };
  const { i18n, t } = useTranslation();
  const codeForm = useForm<CodeFields>({ defaultValues: { code: '' } });
  const [setup, setSetup] = useState<SetupTotp200 | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [codeInputError, setCodeInputError] = useState<'required' | 'incomplete' | null>(null);
  const codeFieldInvalid = Boolean(
    codeInputError || (error && enrollmentErrorKey(error) === 'auth.errorInvalidCode'),
  );
  const codeDigits = new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language).format(6);

  const setupTotp = useSetupTotp({
    mutation: {
      onError: setError,
      onSuccess: async (response) => {
        if (response.status !== 200) return;
        setError(null);
        setSetup(response.data);
        setQrCode(
          await QRCode.toDataURL(response.data.otpauthUri, {
            errorCorrectionLevel: 'M',
            margin: 2,
            width: 220,
          }),
        );
      },
    },
  });
  const verifyTotp = useVerifyTotpSetup({
    mutation: {
      onError: setError,
      onSuccess: (response) => {
        if (response.status !== 200) return;
        setError(null);
        setRecoveryCodes(response.data.recoveryCodes);
      },
    },
  });
  const passkey = useMutation({
    mutationFn: async () => {
      if (!window.PublicKeyCredential) throw new Error('WebAuthn is unavailable');
      const optionsResponse = await createPasskeyRegistrationOptions();
      const credential = await navigator.credentials.create({
        publicKey: toCredentialCreationOptions(optionsResponse.data),
      });
      if (!(credential instanceof PublicKeyCredential)) throw new Error('No credential returned');
      return verifyPasskeyRegistration({
        displayName: t('auth.passkeyDeviceName'),
        response: serializeRegistrationCredential(credential),
      });
    },
    onError: setError,
    onSuccess: continueToManagement,
  });

  if (recoveryCodes) {
    return (
      <AuthPage>
        <Link aria-label={t('brand.name')} className="brand-mark auth-brand" to="/">
          <span aria-hidden="true">S</span>
          {t('brand.name')}
        </Link>
        <Card className="auth-card auth-card-wide">
          <CardContent>
            <ShieldCheck aria-hidden="true" className="auth-enrollment-icon" size={34} />
            <h1>{t('auth.recoveryCodesTitle')}</h1>
            <p className="auth-description">{t('auth.recoveryCodesDescription')}</p>
            <ol className="auth-recovery-codes">
              {recoveryCodes.map((code) => (
                <li key={code}><code dir="ltr">{code}</code></li>
              ))}
            </ol>
            <Button
              leftIcon={<Copy aria-hidden="true" size={16} />}
              onClick={() => void navigator.clipboard?.writeText(recoveryCodes.join('\n'))}
              size="sm"
              type="button"
              variant="outline"
            >
              {t('auth.copyRecoveryCodes')}
            </Button>
            <Button onClick={continueToManagement} size="lg" type="button">
              {t('auth.savedRecoveryCodes')}
            </Button>
          </CardContent>
        </Card>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <Link aria-label={t('brand.name')} className="brand-mark auth-brand" to="/">
        <span aria-hidden="true">S</span>
        {t('brand.name')}
      </Link>
      <Card className="auth-card auth-card-wide">
        <CardContent>
          <p className="eyebrow">{t('auth.enrollmentEyebrow')}</p>
          <h1>{t('auth.enrollmentTitle')}</h1>
          <p className="auth-description">{t('auth.enrollmentDescription')}</p>

          {!setup ? (
            <div className="auth-enrollment-methods">
              <SliderAsyncButton
                leftIcon={<Fingerprint aria-hidden="true" size={18} />}
                onClick={() => passkey.mutate()}
                pending={passkey.isPending}
                size="lg"
                type="button"
              >
                {t('auth.enrollPasskey')}
              </SliderAsyncButton>
              <SliderAsyncButton
                leftIcon={<Smartphone aria-hidden="true" size={18} />}
                onClick={() => setupTotp.mutate()}
                pending={setupTotp.isPending}
                size="lg"
                type="button"
                variant="outline"
              >
                {t('auth.enrollAuthenticator')}
              </SliderAsyncButton>
            </div>
          ) : (
            <div className="auth-totp-setup">
              <p>{t('auth.scanQrCode')}</p>
              {qrCode ? (
                <img alt={t('auth.qrCodeAlt')} height="220" src={qrCode} width="220" />
              ) : null}
              <div className="auth-manual-secret">
                <span>{t('auth.manualSecret')}</span>
                <code dir="ltr">{setup.manualSecret}</code>
              </div>
              <form
                noValidate
                onSubmit={(event) =>
                  void codeForm.handleSubmit(({ code }) => {
                    if (code.trim().length !== 6) {
                      setCodeInputError(code.trim() ? 'incomplete' : 'required');
                      return;
                    }
                    setCodeInputError(null);
                    setError(null);
                    verifyTotp.mutate({
                      data: { code: code.trim(), credentialId: setup.credentialId },
                    });
                  })(event)
                }
              >
                <Controller
                  control={codeForm.control}
                  name="code"
                  render={({ field }) => (
                    <OneTimeCodeField
                      id="totp-enrollment-code"
                      label={t('auth.verificationCode')}
                      describedBy={error || codeInputError ? 'totp-enrollment-feedback' : undefined}
                      invalid={codeFieldInvalid}
                      length={6}
                      name={field.name}
                      onBlur={field.onBlur}
                      onChange={(value) => {
                        setCodeInputError(null);
                        setError(null);
                        field.onChange(value);
                      }}
                      inputRef={field.ref}
                      disabled={verifyTotp.isPending}
                      value={field.value}
                    />
                  )}
                />
                <SliderAsyncButton pending={verifyTotp.isPending} size="lg" type="submit">
                  {t('auth.finishEnrollment')}
                </SliderAsyncButton>
              </form>
            </div>
          )}

          {error || codeInputError ? (
            <p
              aria-live="polite"
              className="auth-error"
              id={setup ? 'totp-enrollment-feedback' : undefined}
              role="alert"
            >
              {codeInputError
                ? t(
                    codeInputError === 'required'
                      ? 'auth.errorCodeRequired'
                      : 'auth.errorCodeIncomplete',
                    { digits: codeDigits },
                  )
                : t(enrollmentErrorKey(error))}
            </p>
          ) : null}
          <p className="auth-policy-hint">{t('auth.strongMfaHint')}</p>
        </CardContent>
      </Card>
    </AuthPage>
  );
}
